/**
 * The day's crew and plant on one construction front.
 *
 * Logged by the supervisor from the DPR: who was there, what plant ran and for
 * how long, what it produced, what stopped it. Editable until it is submitted,
 * then locked.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Plus, Trash2, Lock, LockOpen, Users, Truck, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const blankCrew = () => ({ trade: '', headcount: '', hours: '' });
const blankPlant = () => ({ equipment: '', units: '', operatingHours: '', idleHours: '',
                            breakdownHours: '', standbyHours: '' });
const blankDown = () => ({ reason: 'WEATHER', hours: '', note: '' });

const num = (v) => (v === '' || v == null ? 0 : Number(v) || 0);

export function SiteReturnSheet({ open, onOpenChange, projectId, activity, date, onSaved }) {
  const { user } = useAuth();
  const canReopen = ['SUPERADMIN', 'PLATFORM_ADMIN', 'ORG_ADMIN'].includes(user?.platformRole)
    || ['admin', 'owner'].includes(user?.role);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [locked, setLocked] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [form, setForm] = useState({ crew: [blankCrew()], plant: [blankPlant()],
                                     downtime: [], quantityDone: '', uom: '', remarks: '' });

  const actId = activity?.id;

  useEffect(() => {
    if (!open || !actId) return;
    let dead = false;
    setLoading(true);
    api.get(`/v1/projects/${projectId}/site-returns/activities/${actId}`, { params: { date } })
      .then(({ data }) => {
        if (dead) return;
        setReasons(data.reasons || []);
        const r = data.return;
        setLocked(!!r?.submitted);
        setForm({
          crew: r?.crew?.length ? r.crew : [blankCrew()],
          plant: r?.plant?.length ? r.plant : [blankPlant()],
          downtime: r?.downtime || [],
          quantityDone: r?.quantityDone ?? '',
          uom: r?.uom || data.activity?.uom || '',
          remarks: r?.remarks || '',
        });
      })
      .catch(e => toast.error(e?.response?.data?.detail || 'Could not open the day'))
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [open, actId, projectId, date]);

  const setLine = (key, i, field, value) => setForm(f => {
    const rows = [...f[key]];
    rows[i] = { ...rows[i], [field]: value };
    return { ...f, [key]: rows };
  });
  const addLine = (key, blank) => setForm(f => ({ ...f, [key]: [...f[key], blank()] }));
  const dropLine = (key, i) => setForm(f => ({ ...f, [key]: f[key].filter((_, j) => j !== i) }));

  const payload = useCallback(() => ({
    crew: form.crew.filter(c => c.trade || num(c.headcount))
      .map(c => ({ trade: c.trade, headcount: num(c.headcount), hours: num(c.hours) })),
    plant: form.plant.filter(p => p.equipment || num(p.units))
      .map(p => ({ equipment: p.equipment, units: num(p.units),
                   operatingHours: num(p.operatingHours), idleHours: num(p.idleHours),
                   breakdownHours: num(p.breakdownHours), standbyHours: num(p.standbyHours) })),
    downtime: form.downtime.filter(d => num(d.hours) || d.note)
      .map(d => ({ reason: d.reason, hours: num(d.hours), note: d.note })),
    quantityDone: num(form.quantityDone),
    uom: form.uom,
    remarks: form.remarks,
  }), [form]);

  const reopen = async () => {
    setSaving(true);
    try {
      await api.post(`/v1/projects/${projectId}/site-returns/activities/${actId}/reopen`,
                     null, { params: { date } });
      setLocked(false);
      toast.success('Day reopened');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reopen the day');
    } finally {
      setSaving(false);
    }
  };

  const save = async (submit) => {
    setSaving(true);
    try {
      await api.put(`/v1/projects/${projectId}/site-returns/activities/${actId}`,
                    payload(), { params: { date } });
      if (submit) {
        await api.post(`/v1/projects/${projectId}/site-returns/activities/${actId}/submit`,
                       null, { params: { date } });
        setLocked(true);
      }
      toast.success(submit ? 'Day submitted and locked' : 'Return saved');
      onSaved?.();
      if (submit) onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the return');
    } finally {
      setSaving(false);
    }
  };

  const manhours = form.crew.reduce((s, c) => s + num(c.headcount) * num(c.hours), 0);
  const headcount = form.crew.reduce((s, c) => s + num(c.headcount), 0);
  const opHours = form.plant.reduce((s, p) => s + num(p.operatingHours), 0);
  const plantHours = form.plant.reduce((acc, p) => acc + num(p.operatingHours) + num(p.idleHours)
    + num(p.breakdownHours) + num(p.standbyHours), 0);
  const utilisation = plantHours ? ((opHours / plantHours) * 100).toFixed(1) : null;
  const downtime = form.downtime.reduce((acc, d) => acc + num(d.hours), 0);
  const productivity = manhours && num(form.quantityDone)
    ? (num(form.quantityDone) / manhours).toFixed(3) : null;

  const cell = 'h-8 text-xs';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[560px] sm:max-w-[560px] overflow-y-auto"
                    data-testid="site-return-sheet">
        <SheetHeader>
          <SheetTitle className="text-sm">
            Crew &amp; plant · {date}
            <span className="block text-[11px] font-normal text-muted-foreground mt-0.5">
              {activity?.activityId} — {activity?.activityName}
            </span>
          </SheetTitle>
        </SheetHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-8">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the day…
          </div>
        ) : (
          <div className="space-y-4 py-3">
            {locked && (
              <div className="flex items-center gap-2 text-[11px] rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-amber-700"
                   data-testid="site-return-locked">
                <Lock className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1">
                  This day has been submitted and is locked.
                  {canReopen ? ' You can reopen it to correct a figure.'
                             : ' A project administrator can reopen it.'}
                </span>
                {canReopen && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px]"
                          onClick={reopen} disabled={saving} data-testid="site-return-reopen-btn">
                    <LockOpen className="w-3 h-3 mr-1" />Reopen
                  </Button>
                )}
              </div>
            )}

            <section>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1">
                <Users className="w-3.5 h-3.5" />Crew on the front
              </div>
              <div className="flex gap-1.5 mb-1 text-[9px] uppercase tracking-wide text-muted-foreground">
                <span className="flex-1">Trade</span><span className="w-20">Men</span>
                <span className="w-20">Hours</span><span className="w-9" />
              </div>
              {form.crew.map((c, i) => (
                <div key={i} className="flex gap-1.5 mb-1" data-testid={`site-return-crew-${i}`}>
                  <Input className={`${cell} flex-1`} placeholder="Trade" value={c.trade} disabled={locked}
                         onChange={e => setLine('crew', i, 'trade', e.target.value)} />
                  <Input className={`${cell} w-20`} placeholder="Men" inputMode="decimal" value={c.headcount}
                         disabled={locked} onChange={e => setLine('crew', i, 'headcount', e.target.value)} />
                  <Input className={`${cell} w-20`} placeholder="Hours" inputMode="decimal" value={c.hours}
                         disabled={locked} onChange={e => setLine('crew', i, 'hours', e.target.value)} />
                  <Button variant="ghost" size="sm" className="h-8 px-2" disabled={locked}
                          onClick={() => dropLine('crew', i)} aria-label="Remove crew line">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={locked}
                      onClick={() => addLine('crew', blankCrew)} data-testid="site-return-add-crew">
                <Plus className="w-3 h-3 mr-1" />Add trade
              </Button>
            </section>

            <section>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1">
                <Truck className="w-3.5 h-3.5" />Plant on the front
              </div>
              {form.plant.map((p, i) => (
                <div key={i} className="space-y-1 mb-2" data-testid={`site-return-plant-${i}`}>
                  <div className="flex gap-1.5">
                    <Input className={`${cell} flex-1`} placeholder="Equipment" value={p.equipment}
                           disabled={locked} onChange={e => setLine('plant', i, 'equipment', e.target.value)} />
                    <Input className={`${cell} w-16`} placeholder="Nos" inputMode="decimal" value={p.units}
                           disabled={locked} onChange={e => setLine('plant', i, 'units', e.target.value)} />
                    <Button variant="ghost" size="sm" className="h-8 px-2" disabled={locked}
                            onClick={() => dropLine('plant', i)} aria-label="Remove plant line">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="flex gap-1.5">
                    {[['operatingHours', 'Operating'], ['idleHours', 'Idle'],
                      ['breakdownHours', 'Breakdown'], ['standbyHours', 'Standby']].map(([f, label]) => (
                      <label key={f} className="flex-1">
                        <span className="block text-[9px] uppercase tracking-wide text-muted-foreground">{label} hrs</span>
                        <Input className={`${cell} w-full`} placeholder="0" inputMode="decimal"
                               value={p[f]} disabled={locked}
                               onChange={e => setLine('plant', i, f, e.target.value)} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={locked}
                      onClick={() => addLine('plant', blankPlant)} data-testid="site-return-add-plant">
                <Plus className="w-3 h-3 mr-1" />Add equipment
              </Button>
            </section>

            <section>
              <div className="text-[11px] font-semibold mb-1">Quantity completed today</div>
              <div className="flex gap-1.5">
                <Input className={`${cell} w-32`} placeholder="Quantity" inputMode="decimal"
                       value={form.quantityDone} disabled={locked}
                       data-testid="site-return-quantity"
                       onChange={e => setForm(f => ({ ...f, quantityDone: e.target.value }))} />
                <Input className={`${cell} w-24`} placeholder="UoM" value={form.uom} disabled={locked}
                       onChange={e => setForm(f => ({ ...f, uom: e.target.value }))} />
              </div>
            </section>

            <section>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold mb-1">
                <AlertTriangle className="w-3.5 h-3.5" />What stopped the work
              </div>
              {form.downtime.map((d, i) => (
                <div key={i} className="flex gap-1.5 mb-1" data-testid={`site-return-downtime-${i}`}>
                  <select className="h-8 text-xs rounded-md border bg-transparent px-1 flex-1"
                          value={d.reason} disabled={locked}
                          onChange={e => setLine('downtime', i, 'reason', e.target.value)}>
                    {reasons.map(r => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
                  </select>
                  <Input className={`${cell} w-20`} placeholder="Hours" inputMode="decimal" value={d.hours}
                         disabled={locked} onChange={e => setLine('downtime', i, 'hours', e.target.value)} />
                  <Input className={`${cell} flex-1`} placeholder="Note" value={d.note} disabled={locked}
                         onChange={e => setLine('downtime', i, 'note', e.target.value)} />
                  <Button variant="ghost" size="sm" className="h-8 px-2" disabled={locked}
                          onClick={() => dropLine('downtime', i)} aria-label="Remove downtime line">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" className="h-7 text-[11px]" disabled={locked}
                      onClick={() => addLine('downtime', blankDown)} data-testid="site-return-add-downtime">
                <Plus className="w-3 h-3 mr-1" />Add a reason
              </Button>
            </section>

            <section>
              <div className="text-[11px] font-semibold mb-1">Remarks</div>
              <Textarea rows={2} className="text-xs" value={form.remarks} disabled={locked}
                        data-testid="site-return-remarks"
                        onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} />
            </section>

            <div className="text-[10.5px] font-mono flex flex-wrap gap-x-3 text-muted-foreground"
                 data-testid="site-return-totals">
              <span>crew {headcount}</span>
              <span>manhours {manhours.toFixed(1)}</span>
              <span>op hrs {opHours.toFixed(1)}</span>
              {utilisation && <span>plant util {utilisation}%</span>}
              {downtime > 0 && <span>downtime {downtime.toFixed(1)}h</span>}
              {productivity && <span>productivity {productivity} /mh</span>}
            </div>

            {!locked && (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => save(false)} disabled={saving}
                        data-testid="site-return-save-btn">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}Save draft
                </Button>
                <Button size="sm" onClick={() => save(true)} disabled={saving}
                        data-testid="site-return-submit-btn">
                  <Lock className="w-3.5 h-3.5 mr-1.5" />Submit &amp; lock the day
                </Button>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default SiteReturnSheet;
